// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {createBannerShownEventFor} from '../helpers';
import {BannerAttrs, BannerJsxProps, BannerSize, BannerTheme, BannerType, DropdownType} from '../types';
import css from '../index.inline.styl';
import {template} from './template';

interface DropdownBannerState {
  isDropdownOpen: boolean;
}

const DROPDOWN_TYPE_TO_EVENT_TYPE_MAP = new Map<DropdownType, string>([
  [DropdownType.ProjectSelector, `copyDemoToProject`],
]);

export class DropdownBanner extends Component<DropdownBannerState, BannerAttrs> {
  static get attrsSchema(): AttrsSchema<BannerAttrs> {
    return {
      ...super.attrsSchema,
      'body-text': `string`,
      ctas: `json`,
      dropdown: `json`,
      'footer-text': `string`,
      'header-text': `string`,
      icon: {type: `string`, default: `from-theme`},
      size: {type: `string`, enum: Object.values(BannerSize), default: BannerSize.Medium},
      theme: {type: `string`, enum: Object.values(BannerTheme), default: BannerTheme.Info},
      tooltip: `json`,
      'show-close-button': `boolean`,
      type: {type: `string`, enum: Object.values(BannerType), default: BannerType.Floating},
      'banner-tracking-type': `string`,
      'img-src': `string`,
      multiline: `boolean`,
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {
        isDropdownOpen: false,
      },
      helpers: {
        /**
         * Generically handles menu item selections from any of the banner's dropdowns by
         * propagting the event to listeners. Note that the event fired by this method
         * DOES bubble and IS composed. This allows it to easily escape the awkward way
         * that the mp-report-banners element is constructed if parents of that element
         * need to listen for this event.
         */
        handleMenuItemSelected: (dropType: DropdownType, ev: CustomEvent): void => {
          if (!DROPDOWN_TYPE_TO_EVENT_TYPE_MAP.has(dropType)) {
            return;
          }

          this.helpers.setDropdownOpen(false);
          const newType: string = DROPDOWN_TYPE_TO_EVENT_TYPE_MAP.get(dropType);
          ev.stopPropagation();
          this.dispatchEvent(
            new CustomEvent(newType, {
              bubbles: true,
              cancelable: true,
              composed: true,
              detail: ev.detail,
            }),
          );
        },
        /**
         * Sets whether the dropdown is open or not, based on the given boolean.
         */
        setDropdownOpen: (isOpen: boolean): void => {
          this.update({isDropdownOpen: isOpen});
        },
      },
    };
  }

  public connectedCallback(): void {
    super.connectedCallback();
    requestAnimationFrame(() => {
      this.dispatchEvent(createBannerShownEventFor(this));
    });
  }
}

export const DropdownBannerEl = defineAndMakeJsxFactory<BannerJsxProps>(`mp-dropdown-banner`, DropdownBanner);
export const DropdownBannerReact = panel2react<BannerJsxProps>(`mp-dropdown-banner`, DropdownBanner);
